/*
 * Reference price ranges for page copy (owner review fix, 8 Oct 2026).
 *
 * The FAQ cost answer is BUILT from engine numbers, never typed:
 *  - `data/referenceRanges.json` is written at build time by `seo/fetch-reference-ranges.js` from
 *    GET /api/pricing/reference-ranges (the calculator's own engine pricing one fixed reference home).
 *    It is what the pre-rendered HTML, the FAQPage JSON-LD and the first client render use, so the
 *    snapshot and the hydrated page agree (no hydration mismatch).
 *  - After hydration `LiveCostAnswer` (components/Shared.js) re-fetches the endpoint and swaps in the
 *    live numbers, so a catalog edit shows on the page within minutes without a rebuild.
 *  - If the JSON ever lacks a system, the sentence simply omits it — nothing is invented.
 */
import REFERENCE_RANGES from '../data/referenceRanges.json';

export const REFERENCE_RANGES_PATH = '/pricing/reference-ranges';
export { REFERENCE_RANGES };

const money = (n) => (typeof n === 'number' && Number.isFinite(n) ? `$${Math.round(n).toLocaleString('en-US')}` : null);
const range = (s) => (s && money(s.low) && money(s.high) ? `${money(s.low)}\u2013${money(s.high)}` : null);

// Sentence order (matches the old hand-written answer): furnace, heat pump, furnace + AC, ductless.
const ORDER = ['furnace', 'heat_pump', 'furnace_ac', 'ductless'];
const NAMES = {
  furnace: 'a gas furnace replacement',
  heat_pump: 'a ducted heat pump',
  furnace_ac: 'furnace plus AC',
  ductless: 'a multi-zone ductless system',
};

/** The FAQ "What does a new HVAC installation cost" answer, from a reference-ranges payload. */
export const costAnswer = (data = REFERENCE_RANGES) => {
  const systems = Array.isArray(data?.systems) ? data.systems : [];
  const byKey = Object.fromEntries(systems.map((s) => [s.key, s]));
  const overall = data?.overall && money(data.overall.low) && money(data.overall.high) ? `${money(data.overall.low)} and ${money(data.overall.high)}` : null;
  const gasHome = data?.home?.answers?.current_fuel === 'natural_gas';
  const parts = ORDER.map((k, i) => {
    const r = range(byKey[k]);
    if (!r) return null;
    const note = k === 'heat_pump' && gasHome ? ' (including the gas-to-electric conversion scope)' : '';
    return `${NAMES[k]} ${i === 0 ? 'runs about ' : ''}${r}${note}`;
  }).filter(Boolean);

  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}` : parts[0] || '';
  const opening = overall
    ? `Most complete residential installations in Western Washington land between ${overall} depending on system type, home size, and existing ductwork.`
    : 'The installed price of a complete residential system depends on system type, home size, and existing ductwork.';
  const detail = list
    ? ` On a typical 1,500\u20132,000 sq ft home with gas heat and usable ducts, ${list}, before Washington sales tax and any utility rebate.`
    : '';
  return `${opening}${detail} These figures come from the same pricing engine as our pre-estimate calculator, which gives you a personalized range for your own home in about two minutes; we confirm exact pricing after an on-site measure and load calculation.`;
};
